/**
 * Checks if a resistance value represents an Open Loop (OL / Overload) condition.
 */
export function isOpenLoop(resistance?: any): boolean {
  if (resistance == null) return false;
  if (typeof resistance === "string") {
    return resistance.trim().toUpperCase() === "OL";
  }
  return false;
}

/**
 * Formats resistance values consistently across the application.
 * Returns:
 * - "OL" if the sensor reading is Open Loop.
 * - "—" if value is missing/null/undefined.
 * - e.g. "1.23 Ω" (or without unit "1.23" if includeUnit=false).
 */
export function formatResistance(
  resistance?: any,
  includeUnit = true
): string {
  if (resistance === undefined || resistance === null || resistance === "") {
    return "—";
  }
  if (isOpenLoop(resistance)) {
    return "OL";
  }
  const num = parseFloat(resistance);
  if (isNaN(num)) {
    return String(resistance);
  }
  return includeUnit ? `${num.toFixed(2)} Ω` : num.toFixed(2);
}

/**
 * Checks if a sensor resistance is considered critical.
 */
export function isResistanceCritical(
  resistance?: any,
  criticalThreshold?: number | null
): boolean {
  if (isOpenLoop(resistance)) {
    return true;
  }
  if (resistance == null || resistance === "") {
    return false;
  }
  const num = parseFloat(resistance);
  if (isNaN(num)) {
    return false;
  }
  const effectiveThreshold =
    criticalThreshold != null && !isNaN(Number(criticalThreshold))
      ? Number(criticalThreshold)
      : 3.0;
  return num >= effectiveThreshold;
}

export function getSensorStatus(
  resistance?: number | string | null,
  lastUpdatedAt?: string | number | null,
  ground?: number | null,
  criticalThreshold?: number | null
): string {
    if (!lastUpdatedAt) return "Inactive";
    
    // Handle both ISO strings and Unix timestamps (seconds or milliseconds)
    let dateValue: number | string = lastUpdatedAt;
    
    if (typeof dateValue === 'number' || !isNaN(Number(dateValue))) {
       let num = Number(dateValue);
       // Distinguish between seconds and milliseconds
       // 20000000000 is year 2603, so timestamp in seconds will be smaller.
       if (num < 20000000000) {
          num = num * 1000;
       }
       dateValue = num;
    }

    const lastUpdate = new Date(dateValue);
    const now = new Date();
    
    // Inactive if older than 5 minutes
    if ((now.getTime() - lastUpdate.getTime()) / (1000 * 60) > 5) {
      return "Inactive";
    }

    // Default to Inactive if no resistance/value is available
    if (resistance == null && ground == null) return "Inactive";

    // Open Loop (OL) condition: critical open loop / sensor disconnected
    if (isOpenLoop(resistance)) {
      return "Critical";
    }
    
    // 1. Evaluate Inactive states
    if (ground != null && Number(ground) >= 99) return "Inactive";
    if (resistance != null && !isNaN(Number(resistance)) && Number(resistance) >= 99) return "Inactive";
  
    // 2. Evaluate Critical state (Resistance is above or equal to threshold, but below 99)
    // Default to 3.0 for newly created sensors if no site threshold is configured
    const effectiveThreshold =
      criticalThreshold != null && !isNaN(Number(criticalThreshold))
        ? Number(criticalThreshold)
        : 3.0;

    const numResistance = Number(resistance);
    if (resistance != null && !isNaN(numResistance) && numResistance >= effectiveThreshold) {
      return "Critical";
    }
    
    // 3. Evaluate Active state (Resistance is below threshold, or Ground is below 99 and Resistance is null)
    return "Active";
  }
  
  export const statusColorMap: Record<string, string> = {
    Active: "bg-green-500",
    Critical: "bg-red-500",
    Inactive: "bg-secondary",
  };
  